import { PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import { Box, Slide, ThemeProvider, Toolbar, useMediaQuery } from '@mui/material'
import { TopNavbar } from '../navigation/top-navbar'
import gaiaTheme from '../theme';
import { LEFT_NAV_COLLAPSED_WIDTH, LEFT_NAV_EXPANDED_WIDTH, LeftNavbar } from '../navigation/left-navbar';
import { useLocation, useSearchParams } from 'react-router-dom';
import { UserDetailsContext } from '../../context/user-details/user-details';
import { UserNotificationsContext } from '../../context/user-notifications/user-notifications';
import { WarningBanner } from '../warning-banner';
import { TermsAndConditionsBanner } from '../terms-and-conditions-banner';
import { FactorYearReviewBanner } from '../factor-year-review-banner';
import { ReportDeadlineBanner } from '../report-deadline-banner';
import { InnerLeftNavbar } from '../navigation/inner-left-navbar';
import { PartnerBanner } from '../partner-banner';
import UserDetailsService from '../../shared/services/user-details/user-details.service';
import { ActionsNav } from '../navigation/actions-nav';
import { CustomThemeContext } from '../../context/theme/custom-theme-context';
import { usePdfExportMode } from '../../hooks/use-pdf-export-mode';
import { RightChromeLayoutContext } from '../../context/right-chrome-layout/right-chrome-layout-context';

// Storage can be blocked entirely (privacy modes throw a SecurityError on the
// localStorage getter itself), and the shell must not crash over a nav preference.
// Collapsed is the default: the labelled rail is opt-in via the chevron.
const readStoredLeftNavExpanded = (): boolean => {
  try {
    return localStorage.getItem('gaia.leftNavExpanded') === 'true';
  } catch {
    return false;
  }
};

const writeStoredLeftNavExpanded = (expanded: boolean): void => {
  try {
    localStorage.setItem('gaia.leftNavExpanded', String(expanded));
  } catch {
    // Storage blocked: the choice simply does not persist.
  }
};

export const SiteLayout = ({ children }: PropsWithChildren) => {
  const userDetailsService = new UserDetailsService()
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const customThemeContext = useContext(CustomThemeContext);
  const userNotificationsContext = useContext(UserNotificationsContext);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTopNav, setShowTopNav] = useState(false)
  const [showLeftNav, setShowLeftNav] = useState(false)
  const [theme, setTheme] = useState(customThemeContext.theme);
  const [showInnerLeftNavigation, setShowInnerLeftNavigation] = useState(false)
  // The fixed banners share the same top offset; when the factor-year banner is up, the
  // deadline banner yields (one banner at a time, most actionable first).
  const [factorYearBannerVisible, setFactorYearBannerVisible] = useState(false)
  // const [theme, setTheme] = useState(gaiaTheme)
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  // Below md the desktop chrome still renders (isMobileMode only flips at sm), but an
  // expanded rail plus the 240px inner nav would crush the content, so the rail is
  // forced collapsed there and the expand toggle hidden. The stored preference is
  // untouched: widening the window restores it.
  const isCompactDesktop = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const [showActionsNav, setShowActionsNav] = useState<boolean | undefined>()
  const [showCopilotNav, setShowCopilotNav] = useState(false)
  // Labelled left rail, defaulting to expanded. The key is never user-facing.
  const [leftNavExpanded, setLeftNavExpanded] = useState<boolean>(readStoredLeftNavExpanded)
  const pdfExportMode = usePdfExportMode();

  const leftNavExpandedEffective = leftNavExpanded && !isCompactDesktop;

  const toggleLeftNavExpanded = () => {
    setLeftNavExpanded(prev => {
      const next = !prev;
      writeStoredLeftNavExpanded(next);
      return next;
    });
  };

  useEffect(() => {
    setShowTopNav(!location.pathname.startsWith('/login') && !location.pathname.startsWith('/signup') && !location.pathname.startsWith('/reset') && !location.pathname.startsWith('/forgot-password') && !location.pathname.startsWith('/public') && !location.pathname.startsWith('/pdf-table') && !location.pathname.startsWith('/private') && !location.pathname.startsWith('/complete-registration'))
    setShowLeftNav(!location.pathname.startsWith('/login') && !location.pathname.startsWith('/signup') && !location.pathname.startsWith('/email-verification') && !location.pathname.startsWith('/reset') && !location.pathname.startsWith('/forgot-password') && !location.pathname.startsWith('/public') && !location.pathname.startsWith('/pdf-table') && !location.pathname.startsWith('/private') && !location.pathname.startsWith('/complete-sign-up') && !location.pathname.startsWith('/complete-registration'))
    setShowInnerLeftNavigation(location.pathname.startsWith('/settings') || location.pathname.startsWith('/organisation') || location.pathname.startsWith('/emissions') || location.pathname.startsWith('/analytics') || location.pathname.startsWith('/reduction-plan') || location.pathname.startsWith('/surveys') || location.pathname.startsWith('/partner-library') || location.pathname.startsWith('/partner-settings') || location.pathname.startsWith('/reporting'))
    setIsLoggedIn(!!userDetailsContext.userDetails)
  }, [location.pathname, userDetailsContext.userDetails])

  // Returning from an SSO sign-in (or a "Test sign-in") lands back here with
  // ?ssoSignedIn=<provider>. Confirm it with a snackbar, then strip the param.
  useEffect(() => {
    const provider = searchParams.get('ssoSignedIn')
    if (!provider) return
    const providerNames: { [key: string]: string } = {
      entra: 'Microsoft Entra ID',
      google: 'Google',
    }
    const name = providerNames[provider.toLowerCase()] || 'single sign-on'
    userNotificationsContext.openSnackbar({
      open: true,
      message: `Signed in successfully using ${name}`,
      showSuccessTick: true,
    })
    searchParams.delete('ssoSignedIn')
    setSearchParams(searchParams, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  useEffect(() => {
    if (!location.pathname.startsWith('/login') && !location.pathname.startsWith('/signup') && !location.pathname.startsWith('/email-verification') && !location.pathname.startsWith('/reset') && !location.pathname.startsWith('/forgot-password') && !location.pathname.startsWith('/public') && !location.pathname.startsWith('/pdf-table') && !location.pathname.startsWith('/private') && !location.pathname.startsWith('/complete-sign-up')) {
      setShowActionsNav(userDetails?.showActionsNav)
    }

    // if (userDetails?.organisation.theme) {
    //   setTheme(userDetails?.organisation.theme)
    // }

  }, [userDetails, location.pathname])

  useEffect(() => {
    setTheme(customThemeContext.theme)
  }, [customThemeContext.theme])

  const showActionsHandler = () => {
    userDetailsService.toggleActionsNav()
      .then(response => {
        userDetailsContext.forceUpdateUserDetails();
      })
      .catch(err => console.log(err))
  }

  const appBarOffset = showTopNav ? (isMobileMode ? 13 : 8) : 0;
  const hasOrgBanner = !!(userDetailsContext.userDetails?.isLoggedInAsCustomer || (userDetailsContext.userDetails?.isPartner && userDetailsContext.userDetails?.isLoggedInAsClient));
  const isPrivateReportRoute = location.pathname.startsWith('/private/');
  const shouldReserveOrgBannerSpace = hasOrgBanner && !pdfExportMode;
  const orgBannerOffset = shouldReserveOrgBannerSpace
    ? (isPrivateReportRoute ? (isMobileMode ? '105px' : '48px') : '104px')
    : 0;
  const isPublicSurveyRoute = location.pathname.startsWith('/survey/') || location.pathname.startsWith('/survey-respond');
  const hideToolBar =
    userDetailsContext.userDetails?.isLoggedInAsCustomer ||
    (userDetailsContext.userDetails?.isPartner && userDetailsContext.userDetails?.isLoggedInAsClient) ||
    userDetailsContext.userDetails?.hasAcceptedTermsAndConditions === false ||
    isPublicSurveyRoute ||
    [
      "/login",
      "/signup",
      "/email-verification",
      "/reset",
      "/forgot-password",
      "/public",
      "/private",
      "/complete-sign-up",
      "/complete-registration",
    ].some((path) => location.pathname.startsWith(path));

  // Structural term only: the rail width changes on the user's explicit toggle (or on
  // load from the stored preference) and must snap, never animate (see the transition
  // note on the main Box below).
  const leftNavWidthPx = leftNavExpandedEffective ? LEFT_NAV_EXPANDED_WIDTH : LEFT_NAV_COLLAPSED_WIDTH;
  const leftNavChromeWidthPx = showLeftNav && isLoggedIn && !isMobileMode ? leftNavWidthPx : 0;
  const actionsNavWidthPx = showActionsNav && !isMobileMode ? 240 : 0;
  const copilotNavWidthPx = showCopilotNav && !isMobileMode ? 520 : 0;
  const rightNavChromeWidthPx = actionsNavWidthPx + copilotNavWidthPx;

  const rightChromeLayoutValue = useMemo(
    () => ({
      copilotOpen: showCopilotNav && !isMobileMode,
      actionsPaneOpen: Boolean(showActionsNav) && !isMobileMode,
    }),
    [showCopilotNav, showActionsNav, isMobileMode],
  );

  return (
    <ThemeProvider theme={{ ...theme }}>
      <>
        {/* The fixed banners sit under the rail (lower zIndex), so each one starts at the
            rail's right edge and centres in the visible area. 0 on mobile and wherever the
            rail is hidden, which keeps those layouts exactly as before. */}
        <WarningBanner leftOffset={leftNavChromeWidthPx} />
        <PartnerBanner leftOffset={leftNavChromeWidthPx} />
        <TermsAndConditionsBanner leftOffset={leftNavChromeWidthPx} />
        <FactorYearReviewBanner onVisibilityChange={setFactorYearBannerVisible} leftOffset={leftNavChromeWidthPx} />
        <ReportDeadlineBanner suppressed={factorYearBannerVisible} leftOffset={leftNavChromeWidthPx} />
        <Box sx={{ display: 'flex', maxWidth: '100%' }}>
          {showTopNav && showActionsNav !== undefined &&
            <TopNavbar
              showActionsNav={showActionsNav}
              showActionsHandler={showActionsHandler}
              onCopilotOpenChange={setShowCopilotNav}
              leftNavWidth={leftNavChromeWidthPx}
            />}
          {showLeftNav && isLoggedIn && !isMobileMode &&
            <LeftNavbar
              expanded={leftNavExpandedEffective}
              onToggleExpanded={toggleLeftNavExpanded}
              showToggle={!isCompactDesktop}
            />}
          <Box sx={{ mt: 0 }}>
            {showInnerLeftNavigation && !isMobileMode &&
              <Box sx={{ mr: 30, pt: shouldReserveOrgBannerSpace ? 0 : appBarOffset, mt: orgBannerOffset }}>
                <InnerLeftNavbar />
              </Box>
            }
          </Box>
          <Box
            component="main"
            sx={{
              mt: orgBannerOffset,
              marginRight: !isMobileMode ? `${rightNavChromeWidthPx}px` : '0',
              flexGrow: 1, bgcolor: 'background.default',
              // Right-hand chrome (actions/G.AI panels) is reserved via marginRight, which is the
              // property we animate, so it is deliberately excluded from maxWidth. Keeping it out
              // means opening/closing those panels slides smoothly on marginRight alone, while the
              // structural terms below (left rail 56/240px + inner nav 240px) snap instantly on
              // load / route change instead of animating the content width in, which read as the
              // cards "growing in".
              maxWidth: `calc(100vw - ${leftNavChromeWidthPx}px - ${showInnerLeftNavigation && !isMobileMode ? 240 : 0}px)`,
              // Match MUI Drawer Slide *enter*: easeOut + enteringScreen (see @mui/material/Slide)
              transition: (muiTheme) =>
                muiTheme.transitions.create(['margin-right'], {
                  easing: muiTheme.transitions.easing.easeOut,
                  duration: muiTheme.transitions.duration.enteringScreen,
                }),
            }}>
            <RightChromeLayoutContext.Provider value={rightChromeLayoutValue}>
              {!hideToolBar && <Toolbar />}
              {children}
            </RightChromeLayoutContext.Provider>
          </Box>
          {showActionsNav && !isMobileMode && (
            <Slide direction="left" in={showActionsNav} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  position: 'fixed',
                  right: 0,
                  top: 0,
                  height: '100vh',
                  zIndex: 1000,
                }}>
                <ActionsNav />
              </Box>
            </Slide>
          )}
        </Box>
      </>
    </ThemeProvider>
  )
}